import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';
import type { ApiResponse } from '@shared-models';
import { API_CONFIG, ApiConfig } from '../../api-config';

export interface CompanyCountryItem {
  countryId: string;
  name?: string;
  iso?: string;
  highRisk: boolean;
  active: boolean;
  taxHaven: boolean;
  /** ACTIVE | INACTIVE */
  status: string;
}

export interface CompanyCountries {
  companyId: string;
  items: CompanyCountryItem[];
}

export interface CompanyCountriesUpsertPayload {
  items: Array<{
    countryId: string;
    highRisk: boolean;
    active: boolean;
    taxHaven: boolean;
    status: string;
  }>;
}

/**
 * Tab Empresas → Países.
 * GET/PUT `/api/structure/companies/{companyId}/countries`
 */
@Injectable({ providedIn: 'root' })
export class CompanyCountriesService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private url(companyId: string): string {
    return `${this.apiConfig.apiUrl}/structure/companies/${companyId}/countries`;
  }

  get(companyId: string): Observable<CompanyCountries> {
    return this.httpClient
      .get<ApiResponse<CompanyCountries>>(this.url(companyId))
      .pipe(map((response) => this.normalize(response.data, companyId)));
  }

  update(
    companyId: string,
    payload: CompanyCountriesUpsertPayload,
  ): Observable<CompanyCountries> {
    return this.httpClient
      .put<ApiResponse<CompanyCountries>>(this.url(companyId), {
        items: payload.items ?? [],
      })
      .pipe(map((response) => this.normalize(response.data, companyId)));
  }

  private normalize(
    data: CompanyCountries | undefined,
    companyId: string,
  ): CompanyCountries {
    if (!data) {
      return { companyId, items: [] };
    }
    return {
      companyId: data.companyId || companyId,
      items: Array.isArray(data.items)
        ? data.items.map((item) => ({
            countryId: String(item.countryId),
            name: item.name,
            iso: item.iso,
            highRisk: !!item.highRisk,
            active: !!item.active,
            taxHaven: !!item.taxHaven,
            status: (item.status || 'ACTIVE').toUpperCase(),
          }))
        : [],
    };
  }
}
